/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Button, Icon, Input, Label } from 'semantic-ui-react'
import React, { useRef, useState, useEffect } from 'react'
import { useSaveFulfillment } from '../useSaveFulfillment'
import { debounce } from 'lodash'
import { convertOrderItemsToFulfillmentIssues } from '../../../../../../services/Order/Order.service'
import { useTranslation } from 'react-i18next'
import { ultyToast } from '../../../../../Shared/UltyToast/UltyToast'

const formatNumber = (number) => {
    return Number.isInteger(number) ? number : number.toFixed(2)
}

const QuantityUpdater = ({orderId, orderItem, hasReplacement, autofocus, pickingMethod, onSubmit, estimatedCount=1}) => {
    const [quantity, setQuantity] = useState(orderItem.quantity)
    const [inputValue, setInputValue] = useState(undefined)
    const {saveFulfillmentIssuesMutation, foundItemMutation, isPendingFulfillment} = useSaveFulfillment(+orderId, orderItem.id)
    const [t] = useTranslation()
    const inputRef = useRef(null)

    useEffect(() => {
        if (autofocus && inputRef.current && inputRef.current.inputRef && inputRef.current.inputRef.current) {
            inputRef.current.inputRef.current.focus()
        }
    }, [autofocus])

    const pricedByUnit = orderItem?.item?.pricedByUnit
    const sellableUnit = orderItem?.sellableUnit

    const isPricedByWeightOrVolumeAndSoldByCount =
      pricedByUnit?.type !== 'COUNT' &&
      sellableUnit?.type === 'COUNT'

    const debouncedChangeOrderItemQuantity = useRef(debounce((newQuantity) => {
          if (newQuantity === orderItem.initialQuantity) {
              foundItemMutation.mutate({
                  orderItem: {
                      ...orderItem,
                      quantity: newQuantity,
                  },
                  pickingMethod: pickingMethod || 'MANUAL',
              })
          } else {
              saveFulfillmentIssuesMutation.mutate({
                  fulfillmentIssues: convertOrderItemsToFulfillmentIssues(
                    [{...orderItem, quantity: newQuantity}],
                    [],
                  ),
              })
          }
      }, 1000),
    )

    const handleDebouncedQuantityChange = (newQuantity) => {
        setQuantity(newQuantity)
        debouncedChangeOrderItemQuantity.current(newQuantity)
    }

    if (!orderItem.item || (!orderItem.item.pricedByUnit || orderItem.item.pricedByUnit.type === 'COUNT')) {
        return (
          <div
            css={css`
                display: flex;
            `}
          >
              <Button
                icon
                disabled={quantity <= 0}
                loading={isPendingFulfillment}
                onClick={() => handleDebouncedQuantityChange(quantity - 1)}
              >
                  <Icon name={(quantity - 1) > 0 ? 'minus' : 'trash' }/>
              </Button>
              <div css={css`
                  padding: 0 20px;
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  div {
                      font-weight: bold;
                  }
              `}>
                  {quantity}
              </div>
              <Button
                icon
                disabled={!!(quantity === orderItem.initialQuantity || hasReplacement)}
                loading={isPendingFulfillment}
                onClick={() => handleDebouncedQuantityChange(quantity + 1)}
              >
                  <Icon name="plus"/>
              </Button>
          </div>
        )
    }

    const handleInputChange = (e) => {
        const value = e.target.value

        if (value < 0) {
            ultyToast({ type: 'error', title: t('order.fulfillment.invalid_value') })
            return
        }

        setInputValue(value)
    }

     const getExpectedQuantity = () => {
        const isPricedByWeightOrVolumeAndSoldByCount = orderItem.item.pricedByUnit?.type !== 'COUNT' && orderItem.sellableUnit?.type === 'COUNT'

                if (isPricedByWeightOrVolumeAndSoldByCount) {
                    const sellingOption = orderItem.item.sellingOptions.find(so => so.measurementUnit.type === orderItem.sellableUnit.type)
                    return (sellingOption.conversionRate)* estimatedCount
                }

                return estimatedCount
    }


    const displayWarning = Math.abs(Number(inputValue ?? 0) - (getExpectedQuantity())) > getExpectedQuantity() ;


    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' && !displayWarning) {
            const parsedValue = Number(inputValue)

            if (onSubmit && !isNaN(parsedValue)) {
                onSubmit(parsedValue)
            }
            e.target.blur()
        }
    }



    const getPlaceholder = () => {
       const expected =  getExpectedQuantity();
       //If expected is not an integer, return a formatted string
       if(expected % 1 !== 0) {
        return expected.toFixed(2);
       }
        return expected;
    }

    const getStep = () => {
        const pricingUnit = orderItem.item.pricedByUnit;
        if(!pricedByUnit || pricedByUnit.type === 'COUNT') return 1;
        if(pricingUnit.type === 'WEIGHT' && pricingUnit.unit === 'METRIC_KILOGRAM') return 0.1
        if(pricingUnit.type === 'VOLUME' && pricingUnit.unit === 'METRIC_LITER') return 0.1

        return 1;
    }
    const unitLabel = isPricedByWeightOrVolumeAndSoldByCount ?
                  t(`order.detail.sellable_unit.${orderItem.item.pricedByUnit.type}.unit.${orderItem.item.pricedByUnit.unit}`) :
                  t(`order.detail.sellable_unit.${orderItem.sellableUnit.type}.unit.${orderItem.sellableUnit.unit}`);


    return (
      <div css={css`position: relative`}>
            {displayWarning && (<div css={css`position: absolute; top:-1.2em;left:3px;color:#aa0000; font-size:0.7em; z-index:999`} >{t("order.detail.validation_errors.max",{value:`${formatNumber(getExpectedQuantity()*2)}${unitLabel}`})}</div>)}
          <Input
            error={displayWarning}

            css={css`
                margin-right: .5rem;
                max-width: 170px;

                &.ui.input > input {
                    max-width: 130px;
                }
            `}
            step={getStep()}
            type="number"
            inputMode="numeric"
            labelPosition="right"
            placeholder={getPlaceholder()}
            min={0}
            iconPosition="left"
            loading={isPendingFulfillment}
            disabled={isPendingFulfillment}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            autoFocus={!!autofocus}
            ref={inputRef}
            label={<Label basic >
                {unitLabel}
            </Label>}
          />
      </div>
    )
}

export { QuantityUpdater }
